import { useState } from 'react'
import { Event, getPlatformDisplayName } from '@/lib/calendar/types'
import { Button } from '@/components/ui/shadcn/button'
import { X } from 'lucide-react'
import { WorkspacePreviewLinkedin } from '@/components/workspace/workspace-preview/workspace-preview-linkedin'
import { WorkspacePreviewInstagram } from '@/components/workspace/workspace-preview/workspace-preview-instagram'
import { WorkspacePreviewTiktok } from '@/components/workspace/workspace-preview/workspace-preview-tiktok'
import { WorkspacePreviewYoutube } from '@/components/workspace/workspace-preview/workspace-preview-youtube'
import { WorkspacePreviewFacebook } from '@/components/workspace/workspace-preview/workspace-preview-facebook'
import { type RouterOutput } from '@/config/trpc'
import { type PostContent as LinkedInPostContent } from '@backend/lib/platforms/linkedin'
import { type PostContent as InstagramPostContent } from '@backend/lib/platforms/instagram'
import { type PostContent as TikTokPostContent } from '@backend/lib/platforms/tiktok'
import { type PostContent as YouTubePostContent } from '@backend/lib/platforms/youtube'
import { type PostContent as FacebookPostContent } from '@backend/lib/platforms/facebook'
import { type PlatformType } from '@backend/lib/db/schema/platform'

type AccountData = RouterOutput['platform']['isAuthenticated']
type Account = Extract<AccountData, { isAuthenticated: true }>['accounts'][number]

interface EventEditModalProps {
  event: Event | null
  isOpen: boolean
  onClose: () => void
  onEventUpdate: (updatedEvent: Event) => void
  onEventDelete: (eventId: string) => void
  account?: Account
  platform: PlatformType
  readOnly?: boolean
  /** Removes a published (tracked) post from CASSI — the escape hatch for the
   *  publish lock. Absent for shared (token-based) viewers. */
  onTrackedPostRemove?: (trackedPostId: number) => void
}

// Format date in local timezone (YYYY-MM-DD)
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Parse date string in local timezone (YYYY-MM-DD)
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number)
  // Create date in local timezone (not UTC)
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1)
}

export function EventEditModal({
  event,
  isOpen,
  onClose,
  onEventUpdate,
  onEventDelete,
  account,
  platform: platformType,
  readOnly = false,
  onTrackedPostRemove,
}: EventEditModalProps) {
  const [title, setTitle] = useState(event?.title ?? '')
  const [date, setDate] = useState(event ? formatLocalDate(event.date) : '')
  const [time, setTime] = useState(event?.time ?? '')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)

  // Sync state when event changes
  const [prevEvent, setPrevEvent] = useState(event)
  if (prevEvent !== event && event) {
    setPrevEvent(event)
    setTitle(event.title)
    setDate(formatLocalDate(event.date))
    setTime(event.time)
    setShowDeleteConfirm(false)
    setShowRemoveConfirm(false)
  }

  if (!isOpen || !event) return null

  const handleSave = () => {
    // Parse date in local timezone, then combine with time
    const dateObj = parseLocalDate(date)
    const [hours, minutes] = time.split(':').map(Number)
    dateObj.setHours(hours ?? 0, minutes ?? 0, 0, 0)

    const updatedEvent: Event = {
      ...event,
      title,
      date: dateObj,
      time,
      // Keep platform and platformColor unchanged
    }
    onEventUpdate(updatedEvent)
    onClose()
  }

  // Render post preview based on platform
  const renderPostPreview = () => {
    if (!event.postContent) return null

    switch (platformType) {
      case 'linkedin':
        return (
          <WorkspacePreviewLinkedin
            account={account}
            postContent={event.postContent as LinkedInPostContent}
            _onStartAuth={() => {}}
          />
        )
      case 'instagram':
        return (
          <WorkspacePreviewInstagram
            account={account}
            postContent={event.postContent as InstagramPostContent}
            _onStartAuth={() => {}}
          />
        )
      case 'tiktok':
        return (
          <WorkspacePreviewTiktok
            account={account}
            postContent={event.postContent as TikTokPostContent}
            _onStartAuth={() => {}}
          />
        )
      case 'youtube':
        return (
          <WorkspacePreviewYoutube
            account={account}
            postContent={event.postContent as YouTubePostContent}
            _onStartAuth={() => {}}
          />
        )
      case 'facebook':
        return (
          <WorkspacePreviewFacebook
            account={account}
            postContent={event.postContent as FacebookPostContent}
            _onStartAuth={() => {}}
          />
        )
      default:
        return null
    }
  }

  const handleDelete = () => {
    onEventDelete(event.id)
    setShowDeleteConfirm(false)
    onClose()
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4"
        onClick={onClose}>
        <div
          className="bg-card border-border my-auto flex max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-lg border shadow-lg"
          onClick={(e) => e.stopPropagation()}>
          {/* Left side - Edit form */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-foreground text-lg font-semibold">
                {readOnly ? 'View Post' : 'Edit Event'}
              </h2>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-foreground mb-1 block text-sm font-medium">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={readOnly}
                  className={`bg-background border-border text-foreground w-full rounded-md border px-3 py-2 ${
                    readOnly
                      ? 'cursor-not-allowed opacity-70'
                      : 'focus:ring-primary focus:outline-hidden focus:ring-2'
                  }`}
                  placeholder="Short 5-word description"
                />
              </div>

              <div>
                <label className="text-foreground mb-1 block text-sm font-medium">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={readOnly}
                  className={`bg-background border-border text-foreground w-full rounded-md border px-3 py-2 ${
                    readOnly
                      ? 'cursor-not-allowed opacity-70'
                      : 'focus:ring-primary focus:outline-hidden focus:ring-2'
                  }`}
                />
              </div>

              <div>
                <label className="text-foreground mb-1 block text-sm font-medium">Time</label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  disabled={readOnly}
                  className={`bg-background border-border text-foreground w-full rounded-md border px-3 py-2 ${
                    readOnly
                      ? 'cursor-not-allowed opacity-70'
                      : 'focus:ring-primary focus:outline-hidden focus:ring-2'
                  }`}
                />
              </div>

              <div>
                <label className="text-foreground mb-1 block text-sm font-medium">Platform</label>
                <input
                  type="text"
                  value={getPlatformDisplayName(event.platform)}
                  disabled
                  className="bg-muted border-border text-foreground/50 w-full cursor-not-allowed rounded-md border px-3 py-2"
                />
              </div>

              {!readOnly && (
                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSave} className="flex-1">
                    Save Changes
                  </Button>
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="outline"
                    className="text-red-500 hover:text-red-600">
                    Delete
                  </Button>
                </div>
              )}

              {readOnly && event.link && (
                <div className="pt-4">
                  <Button
                    onClick={() => window.open(event.link!, '_blank', 'noopener,noreferrer')}
                    className="w-full">
                    View on {getPlatformDisplayName(event.platform)}
                  </Button>
                </div>
              )}

              {event.source === 'tracked' && event.trackedPostId != null && onTrackedPostRemove && (
                <div className="pt-2">
                  <Button
                    onClick={() => setShowRemoveConfirm(true)}
                    variant="outline"
                    className="w-full text-red-500 hover:text-red-600">
                    Remove from CASSI
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Right side - Post preview */}
          <div className="border-border min-w-0 flex-1 overflow-y-auto border-l bg-white">
            <div className="flex h-full flex-col p-4">
              <div className="min-h-0 flex-1">
                {event.postContent ? (
                  renderPostPreview()
                ) : (
                  <div className="flex h-full flex-col items-center gap-4 overflow-y-auto p-6">
                    {event.mediaUrl ? (
                      <img
                        src={event.mediaUrl}
                        alt="Post preview"
                        className="max-h-64 max-w-full shrink-0 rounded-lg object-contain"
                        crossOrigin="anonymous"
                      />
                    ) : (
                      <div className="bg-muted flex h-32 w-32 shrink-0 items-center justify-center rounded-lg">
                        <span className="text-foreground text-sm">No preview</span>
                      </div>
                    )}
                    {(event.caption || event.title) && (
                      <p className="text-foreground max-w-sm whitespace-pre-line text-sm">
                        {event.caption || event.title}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          className="z-60 fixed inset-0 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowDeleteConfirm(false)}>
          <div
            className="bg-card border-border w-full max-w-sm rounded-lg border p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="text-foreground mb-3 text-lg font-semibold">Delete Event</h3>
            <p className="text-muted-foreground mb-4 text-sm">
              Are you sure? This will permanently delete this scheduled post and there is no undo.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleDelete}
                className="flex-1 bg-red-600 text-white hover:bg-red-700">
                Delete
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="outline"
                className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Remove-from-CASSI confirmation (tracked posts) */}
      {showRemoveConfirm && event.trackedPostId != null && onTrackedPostRemove && (
        <div
          className="z-60 fixed inset-0 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowRemoveConfirm(false)}>
          <div
            className="bg-card border-border w-full max-w-sm rounded-lg border p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="text-foreground mb-3 text-lg font-semibold">Remove from CASSI</h3>
            <p className="text-muted-foreground mb-4 text-sm">
              This does NOT delete the post on {getPlatformDisplayName(event.platform)} — it only
              removes it from CASSI, including its analytics, and unlocks the workspace it was
              created from. Only do this if the post was deleted on the platform or published by
              mistake.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  onTrackedPostRemove(event.trackedPostId!)
                  setShowRemoveConfirm(false)
                }}
                className="flex-1 bg-red-600 text-white hover:bg-red-700">
                Remove
              </Button>
              <Button
                onClick={() => setShowRemoveConfirm(false)}
                variant="outline"
                className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
